import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    Home, Users, Clock, Calendar, DollarSign, Target, FileText,
    HelpCircle, ClipboardList, User, FolderOpen, Zap, ShieldCheck, LogOut,
    Settings, Ticket, Loader2, AlertCircle, CheckCircle2, Bell, ChevronDown,
    ArrowRight, Megaphone, CheckSquare, Sparkles, Send, Inbox, Plug,
    LogIn, TrendingUp, CalendarDays, Receipt, FileSignature, BarChart3, X
} from 'lucide-react';

// Import the page components
import EmployeesPage from './Employees';
import AttendancePage from './Attendance';
import LeavePage from './Leave';
import PayrollPage from './Payroll';
import PerformancePage from './Performance';
import DocumentsPage from './Documents';
import AIAssistant from './AIAssistant';
import MyAttendancePage from './MyAttendance';
import AccessRequestsPage from './AccessRequests';
import ProfilePage from './Profile';
import SettingsPage from './Settings';
import MyTicketsPage from './MyTickets';
import NotificationsPage from './Notifications';
import { backendApi } from './api/backendApi';
import { formatDurationFromSeconds } from '../utils/formatDuration';
import { colors as DS } from '../design-system';

// ---------------------------------------------------------------------
//  THEME TOKENS, flat editorial surface, deep-forest accent
// ---------------------------------------------------------------------
const T = {
    green: DS.forest,        // #1A3C2B, primary ink-green
    greenDeep: '#122B1F',
    greenTint: '#EDF3EF',
    bg: '#F6F6F3',
    card: '#FFFFFF',
    line: '#E7E6E0',
    lineSoft: '#F0EFEA',
    ink: '#15181C',
    body: '#3F4650',
    muted: '#6B7280',
    faint: '#9CA3AF',
};

const SERIES = {
    present: '#2F7D4F',
    absent: '#D9534F',
    late: '#E0A33E',
    half: '#7E6BE0',
};

// ---------------------------------------------------------------------
//  PRIMITIVES
// ---------------------------------------------------------------------

// Small uppercase tracked label used as every card's title.
const CardTitle = ({ icon: Icon, children, note }) => (
    <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-[15px] h-[15px] flex-shrink-0" style={{ color: T.green }} strokeWidth={2} />}
        <h3 className="hrms-label truncate">{children}</h3>
        {note && <span className="hrms-note truncate">{note}</span>}
    </div>
);

// White panel with hairline border, optional header row and footer link.
const Panel = ({
    title, icon, note, right, footer, onFooter,
    className = '', bodyClass = '', padded = true, children,
}) => (
    <section className={`hrms-card flex flex-col ${className}`}>
        {(title || right) && (
            <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
                <CardTitle icon={icon} note={note}>{title}</CardTitle>
                {right}
            </div>
        )}
        <div className={`${padded ? 'px-5 pb-5' : ''} flex-1 min-w-0 ${bodyClass}`}>{children}</div>
        {footer && (
            <button type="button" onClick={onFooter} className="hrms-card-foot">
                <span>{footer}</span>
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
            </button>
        )}
    </section>
);

const ViewAll = ({ onClick, label = 'View All' }) => (
    <button type="button" onClick={onClick} className="hrms-viewall">
        {label}
        <ArrowRight className="w-3 h-3" strokeWidth={2.2} />
    </button>
);

const EmptyRow = ({ children = 'Nothing here yet' }) => (
    <p className="py-6 text-center text-[13px]" style={{ color: T.faint }}>{children}</p>
);

const StatusPill = ({ status }) => {
    const s = String(status || '').toLowerCase();
    const tone =
        s.includes('approve') || s.includes('paid') || s.includes('process') || s.includes('active') || s.includes('done')
            ? { bg: '#E8F3EC', fg: '#2F7D4F' }
            : s.includes('reject') || s.includes('fail')
                ? { bg: '#FCEAEA', fg: '#C0413D' }
                : { bg: '#F3F2ED', fg: '#6B7280' };
    return (
        <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold whitespace-nowrap"
            style={{ background: tone.bg, color: tone.fg }}>
            {status}
        </span>
    );
};

// ---------------------------------------------------------------------
//  FORMATTERS
// ---------------------------------------------------------------------
const fmtDate = (value) => {
    if (!value) return ',';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const fmtMoney = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return ',';
    return `₹${n.toLocaleString('en-IN')}`;
};

const initials = (text = '') =>
    text.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

const formatSecondsToHMS = (totalSeconds) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const hours = String(Math.floor(s / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const seconds = String(s % 60).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
};

const timeAgo = (isoDate) => {
    if (!isoDate) return '';
    const diffMs = Date.now() - new Date(isoDate).getTime();
    const seconds = Math.max(0, Math.floor(diffMs / 1000));
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
};

const NOTIF_ICONS = {
    attendance: Clock,
    leave: Calendar,
    payroll: DollarSign,
    performance: Target,
    document: FileText,
    ticket: Ticket,
    access_request: ShieldCheck,
    account: User,
};

// ---------------------------------------------------------------------
//  CHARTS
// ---------------------------------------------------------------------

// Leave balance, remaining days per type, from the HRMS backend.
const LeaveBalanceChart = ({ onViewDetails }) => {
    const [data, setData] = useState(null);

    useEffect(() => {
        let active = true;
        backendApi.leave.balance().then((balances) => {
            if (!active) return;
            setData(Array.isArray(balances)
                ? balances.map((b) => ({ label: b.type, days: Number(b.remaining) || 0, color: b.color }))
                : []);
        }).catch(() => active && setData([]));
        return () => { active = false; };
    }, []);

    const rows = data || [];
    const CIRC = 2 * Math.PI * 42;
    const total = rows.reduce((sum, d) => sum + d.days, 0) || 1;
    let acc = 0;

    return (
        <Panel title="Leave Balance" icon={CalendarDays} footer="View Leave Details" onFooter={onViewDetails}>
            {data === null ? (
                <EmptyRow>Loading…</EmptyRow>
            ) : rows.length === 0 ? (
                <EmptyRow>No leave balance available</EmptyRow>
            ) : (
                <div className="flex items-center gap-5">
                    <div className="relative w-[112px] h-[112px] flex-shrink-0">
                        <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                            <circle cx="50" cy="50" r="42" fill="transparent" stroke={T.lineSoft} strokeWidth="13" />
                            {rows.map((item, i) => {
                                const dash = (item.days / total) * CIRC;
                                const seg = (
                                    <circle key={i} cx="50" cy="50" r="42" fill="transparent"
                                        stroke={item.color} strokeWidth="13" strokeLinecap="butt"
                                        strokeDasharray={`${dash} ${CIRC}`} strokeDashoffset={`${-acc}`} />
                                );
                                acc += dash;
                                return seg;
                            })}
                        </svg>
                    </div>
                    <div className="flex-1 space-y-3 min-w-0">
                        {rows.map((item) => (
                            <div key={item.label} className="flex items-center justify-between gap-3">
                                <span className="flex items-center min-w-0">
                                    <span className="w-2 h-2 rounded-full mr-2 flex-shrink-0" style={{ background: item.color }} />
                                    <span className="text-[13px] truncate" style={{ color: T.body }}>{item.label}</span>
                                </span>
                                <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: T.ink }}>
                                    {item.days} days
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Panel>
    );
};

// Attendance overview, this month's records bucketed by status.
const AttendanceOverview = ({ onViewAttendance }) => {
    const [counts, setCounts] = useState(null);

    useEffect(() => {
        let active = true;
        backendApi.attendance.history().then((records) => {
            if (!active) return;
            const now = new Date();
            const tally = { Present: 0, Absent: 0, Late: 0, 'Half Day': 0 };
            (Array.isArray(records) ? records : []).forEach((r) => {
                const d = new Date(r.date);
                if (Number.isNaN(d.getTime())) return;
                if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return;
                const s = String(r.status || 'present').toLowerCase();
                if (s.includes('absent')) tally.Absent += 1;
                else if (s.includes('late')) tally.Late += 1;
                else if (s.includes('half')) tally['Half Day'] += 1;
                else tally.Present += 1;
            });
            setCounts(tally);
        }).catch(() => active && setCounts({ Present: 0, Absent: 0, Late: 0, 'Half Day': 0 }));
        return () => { active = false; };
    }, []);

    const bars = [
        { label: 'Present', value: counts?.Present ?? 0, color: SERIES.present },
        { label: 'Absent', value: counts?.Absent ?? 0, color: SERIES.absent },
        { label: 'Late', value: counts?.Late ?? 0, color: SERIES.late },
        { label: 'Half Day', value: counts?.['Half Day'] ?? 0, color: SERIES.half },
    ];

    const peak = Math.max(10, ...bars.map((b) => b.value));
    const step = Math.ceil(peak / 3 / 5) * 5 || 5;
    const top = step * 3;
    const ticks = [top, step * 2, step, 0];

    return (
        <Panel title="Attendance Overview" icon={BarChart3} note="(This Month)"
            footer="View Attendance" onFooter={onViewAttendance}>
            <div className="flex gap-3 h-[150px]">
                <div className="flex flex-col justify-between py-1 text-[10px] font-medium text-right w-6 flex-shrink-0"
                    style={{ color: T.faint }}>
                    {ticks.map((t) => <span key={t}>{t}</span>)}
                </div>
                <div className="relative flex-1 min-w-0">
                    {ticks.map((t, i) => (
                        <span key={t} className="absolute left-0 right-0 border-t"
                            style={{ borderColor: T.lineSoft, top: `${(i / 3) * 100}%` }} />
                    ))}
                    <div className="relative h-full flex items-end justify-around gap-2 pb-px">
                        {bars.map((b) => (
                            <div key={b.label} className="flex-1 flex flex-col items-center justify-end h-full">
                                <span className="text-[11px] font-bold mb-1" style={{ color: T.ink }}>{b.value}</span>
                                <div className="w-full max-w-[34px] rounded-t-[3px] transition-all"
                                    style={{
                                        height: `${Math.max(b.value > 0 ? 4 : 2, (b.value / top) * 100)}%`,
                                        background: b.color,
                                        opacity: b.value > 0 ? 1 : 0.35,
                                    }} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex gap-2 mt-2 pl-9">
                {bars.map((b) => (
                    <span key={b.label} className="flex-1 text-center text-[10px] font-medium truncate"
                        style={{ color: T.muted }}>{b.label}</span>
                ))}
            </div>
        </Panel>
    );
};

// Performance radar, five-axis pentagon in the module accent.
const PerformanceRadar = ({ onViewPerformance }) => {
    const gridOuter = '50,12 88,38 74,80 26,80 12,38';
    const gridMid = '50,25 76,43 66,72 34,72 24,43';
    const gridInner = '50,37 65,47 59,63 41,63 35,47';
    const dataPoly = '50,18 82,40 70,76 30,76 17,40';

    return (
        <Panel title="My Performance" icon={Target} footer="View Performance" onFooter={onViewPerformance}>
            <div className="flex items-center justify-center h-[172px]">
                <svg viewBox="0 0 100 92" className="w-full h-full">
                    <polygon points={gridOuter} fill="transparent" stroke={T.line} strokeWidth="0.7" />
                    <polygon points={gridMid} fill="transparent" stroke={T.line} strokeWidth="0.7" />
                    <polygon points={gridInner} fill="transparent" stroke={T.line} strokeWidth="0.7" />

                    <line x1="50" y1="50" x2="50" y2="12" stroke={T.line} strokeWidth="0.7" />
                    <line x1="50" y1="50" x2="88" y2="38" stroke={T.line} strokeWidth="0.7" />
                    <line x1="50" y1="50" x2="74" y2="80" stroke={T.line} strokeWidth="0.7" />
                    <line x1="50" y1="50" x2="26" y2="80" stroke={T.line} strokeWidth="0.7" />
                    <line x1="50" y1="50" x2="12" y2="38" stroke={T.line} strokeWidth="0.7" />

                    <polygon points={dataPoly} fill={T.green} fillOpacity="0.14" stroke={T.green} strokeWidth="1.1" />
                    {dataPoly.split(' ').map((p) => {
                        const [x, y] = p.split(',');
                        return <circle key={p} cx={x} cy={y} r="1.5" fill={T.green} />;
                    })}

                    <text x="50" y="8" fontSize="4.6" textAnchor="middle" fill={T.muted} fontWeight="500">Goals</text>
                    <text x="92" y="38" fontSize="4.6" textAnchor="start" fill={T.muted} fontWeight="500">Skills</text>
                    <text x="76" y="86" fontSize="4.6" textAnchor="middle" fill={T.muted} fontWeight="500">Attendance</text>
                    <text x="24" y="86" fontSize="4.6" textAnchor="middle" fill={T.muted} fontWeight="500">Projects</text>
                    <text x="8" y="38" fontSize="4.6" textAnchor="end" fill={T.muted} fontWeight="500">Collaboration</text>
                </svg>
            </div>
        </Panel>
    );
};

// ---------------------------------------------------------------------
//  MODALS
// ---------------------------------------------------------------------
const ModalShell = ({ title, icon: Icon, onClose, children }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(21,24,28,0.45)' }}>
        <div className="w-full max-w-lg rounded-2xl overflow-hidden bg-white"
            style={{ boxShadow: '0 30px 70px -25px rgba(15,25,20,0.55)' }}>
            <div className="px-5 py-4 flex justify-between items-center"
                style={{ background: T.green, color: '#fff' }}>
                <h2 className="text-[15px] font-bold flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4" strokeWidth={2} />}
                    {title}
                </h2>
                <button onClick={onClose} className="opacity-80 hover:opacity-100 transition-opacity">
                    <X className="w-4 h-4" strokeWidth={2.4} />
                </button>
            </div>
            {children}
        </div>
    </div>
);

// Inline success/error banner, in place of the old alert() calls.
const FormFeedback = ({ feedback }) => {
    if (!feedback) return null;
    const ok = feedback.type === 'success';
    return (
        <div className="flex items-center gap-2.5 text-[13px] px-3 py-2.5 rounded-lg border"
            style={ok
                ? { background: '#F0F7F2', color: '#2F7D4F', borderColor: '#CFE4D6' }
                : { background: '#FDF1F1', color: '#B91C1C', borderColor: '#F3D3D3' }}>
            {ok
                ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" strokeWidth={2.2} />
                : <AlertCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2.2} />}
            <span>{feedback.message}</span>
        </div>
    );
};

const RaiseTicketModal = ({ isOpen, onClose }) => {
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message }
    const queryClient = useQueryClient();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const form = e.target;
        const storedUser = backendApi.auth.getStoredUser();
        const payload = {
            title: form.title.value,
            category: form.category.value,
            priority: form.priority.value,
            description: form.description.value,
            status: 'Open',
            raised_by: storedUser?.id,
            raised_by_name: storedUser?.full_name || storedUser?.name,
        };
        setSubmitting(true);
        setFeedback(null);
        try {
            await backendApi.entities.Ticket.create(payload);
            setFeedback({ type: 'success', message: 'Ticket submitted successfully!' });
            queryClient.invalidateQueries({ queryKey: ['my-tickets'] });
            setTimeout(() => {
                onClose();
            }, 1200);
        } catch (err) {
            setFeedback({ type: 'error', message: `Failed to submit ticket: ${err.message || 'Unable to reach the server.'}` });
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalShell title="Raise Support Ticket" icon={HelpCircle} onClose={onClose}>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <FormFeedback feedback={feedback} />

                <div>
                    <label className="hrms-field-label">Ticket Title *</label>
                    <input type="text" name="title" placeholder="Brief description of the issue"
                        className="hrms-input" required />
                </div>

                <div className="flex gap-4">
                    <div className="w-1/2">
                        <label className="hrms-field-label">Category *</label>
                        <select name="category" className="hrms-input" required>
                            <option value="">Select category</option>
                            <option>Technical Issue</option>
                            <option>HR/Payroll</option>
                            <option>Access Request</option>
                        </select>
                    </div>
                    <div className="w-1/2">
                        <label className="hrms-field-label">Priority</label>
                        <select name="priority" className="hrms-input">
                            <option>Medium</option>
                            <option>Low</option>
                            <option>High</option>
                        </select>
                    </div>
                </div>

                <div>
                    <label className="hrms-field-label">Description *</label>
                    <textarea name="description" rows="4" className="hrms-input resize-none"
                        placeholder="Provide detailed information about your issue..." required />
                </div>

                <div className="flex justify-end pt-4 gap-3 border-t" style={{ borderColor: T.lineSoft }}>
                    <button type="button" onClick={onClose} className="hrms-btn-ghost">Cancel</button>
                    <button type="submit" className="hrms-btn-primary" disabled={submitting}>
                        {submitting ? 'Submitting…' : 'Submit Ticket'}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
};

const ApplyLeaveModal = ({ isOpen, onClose }) => {
    const [submitting, setSubmitting] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        const form = e.target;
        const storedUser = backendApi.auth.getStoredUser();
        const start = form.start_date.value;
        const end = form.end_date.value;
        const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1);

        setSubmitting(true);
        setFeedback(null);
        try {
            // This used to only show a success message and throw the form away.
            await backendApi.entities.LeaveRequest.create({
                employee_id: storedUser?.id,
                employee_name: storedUser?.full_name || storedUser?.name,
                leave_type: form.leave_type.value,
                duration: form.duration.value,
                start_date: start,
                end_date: end,
                days,
                reason: form.reason.value,
                status: 'Pending',
            });
            setFeedback({ type: 'success', message: 'Leave application submitted!' });
            setTimeout(() => onClose(), 1200);
        } catch (err) {
            setFeedback({ type: 'error', message: `Failed to apply for leave: ${err.message || 'Unable to reach the server.'}` });
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalShell title="Apply for Leave" icon={Calendar} onClose={onClose}>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <FormFeedback feedback={feedback} />

                <div className="flex gap-4">
                    <div className="w-1/2">
                        <label className="hrms-field-label">Leave Type *</label>
                        <select name="leave_type" className="hrms-input" required>
                            <option>Paid Leave</option>
                            <option>Casual Leave</option>
                            <option>Sick Leave</option>
                        </select>
                    </div>
                    <div className="w-1/2">
                        <label className="hrms-field-label">Duration</label>
                        <select name="duration" className="hrms-input">
                            <option>Full Day</option>
                            <option>First Half</option>
                            <option>Second Half</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-4">
                    <div className="w-1/2">
                        <label className="hrms-field-label">Start Date *</label>
                        <input type="date" name="start_date" className="hrms-input" required />
                    </div>
                    <div className="w-1/2">
                        <label className="hrms-field-label">End Date *</label>
                        <input type="date" name="end_date" className="hrms-input" required />
                    </div>
                </div>

                <div>
                    <label className="hrms-field-label">Reason *</label>
                    <textarea name="reason" rows="4" className="hrms-input resize-none"
                        placeholder="Briefly describe the reason for your leave..." required />
                </div>

                <div className="flex justify-end pt-4 gap-3 border-t" style={{ borderColor: T.lineSoft }}>
                    <button type="button" onClick={onClose} className="hrms-btn-ghost">Cancel</button>
                    <button type="submit" className="hrms-btn-primary" disabled={submitting}>
                        {submitting ? 'Submitting…' : 'Submit Application'}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
};

// ---------------------------------------------------------------------
//  IN-PAGE SECTIONS
// ---------------------------------------------------------------------

const PageFrame = ({ title, subtitle, children }) => (
    <div className="max-w-5xl">
        <h1 className="text-[22px] font-bold tracking-tight" style={{ color: T.ink }}>{title}</h1>
        {subtitle && <p className="text-[13px] mt-1 mb-6" style={{ color: T.muted }}>{subtitle}</p>}
        {children}
    </div>
);

// The full-page notification feed lives in its own module (Notifications.jsx),
// imported above as NotificationsPage — the premium redesign that replaced the
// old inline version. The header bell and this page share the same prop
// contract: { notifications, unreadCount, onMarkAllRead, onOpen }.

// The one sidebar entry with no backing feature, shown honestly rather
// than faked with sample data.
const NotConfiguredPage = ({ title, subtitle, icon: Icon, message }) => (
    <PageFrame title={title} subtitle={subtitle}>
        <Panel>
            <div className="flex flex-col items-center justify-center text-center py-16">
                <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: T.greenTint }}>
                    <Icon className="w-6 h-6" style={{ color: T.green }} strokeWidth={1.8} />
                </span>
                <p className="text-[14px] font-semibold" style={{ color: T.ink }}>Not configured yet</p>
                <p className="text-[13px] mt-1 max-w-sm" style={{ color: T.muted }}>{message}</p>
            </div>
        </Panel>
    </PageFrame>
);

// ---------------------------------------------------------------------
//  MAIN DASHBOARD
// ---------------------------------------------------------------------
export default function EmployeeDashboard({ onLogout, userName = '' }) {
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isClockedIn, setIsClockedIn] = useState(false);
    const [checkInTime, setCheckInTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState('00:00:00');
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [currentPage, setCurrentPage] = useState('Dashboard');

    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
    const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const profileMenuRef = React.useRef(null);
    const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);

    const [isNotifOpen, setIsNotifOpen] = useState(false);
    const notifMenuRef = React.useRef(null);
    // Real, live notifications, fetched from the backend and pushed to in
    // real time via Server-Sent Events (see the effect below). No more
    // static/mock data here.
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Dashboard feed data
    const [employees, setEmployees] = useState(null);
    const [payrolls, setPayrolls] = useState(null);
    const [documents, setDocuments] = useState(null);
    const [leaveRequests, setLeaveRequests] = useState(null);

    const [aiQuestion, setAiQuestion] = useState('');
    const [tasks, setTasks] = useState([
        { id: 1, title: 'Complete KYC Verification', done: false },
        { id: 2, title: 'Update Bank Details', done: false },
        { id: 3, title: 'Submit Expense Report', done: false },
    ]);

    const [toast, setToast] = useState(null);
    const showToast = (message, type = 'progress') => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };

    // Normalize a backend notification row into what this UI renders.
    const mapNotification = (n) => ({
        id: n.id,
        type: n.type,
        audience: n.audience,
        title: n.title,
        message: n.message,
        link: n.link,
        time: timeAgo(n.created_at),
        read: !!n.is_read,
    });

    const refreshNotifications = React.useCallback(async () => {
        const [list, count] = await Promise.all([
            backendApi.notifications.list({ limit: 30 }),
            backendApi.notifications.unreadCount(),
        ]);
        setNotifications(list.map(mapNotification));
        setUnreadCount(count);
    }, []);

    // Initial load + live push (SSE) with a polling fallback for
    // networks/proxies that block long-lived connections.
    useEffect(() => {
        let active = true;
        refreshNotifications();

        const source = backendApi.notifications.stream();
        const handlePush = (event) => {
            if (!active) return;
            try {
                const row = JSON.parse(event.data);
                setNotifications((prev) => [mapNotification(row), ...prev].slice(0, 30));
                setUnreadCount((prev) => prev + 1);
                showToast(row.title, 'progress');
            } catch {
                // Ignore malformed push frames; the next poll will catch up.
            }
        };
        source?.addEventListener('notification', handlePush);
        source?.addEventListener('error', () => {
            // EventSource retries connecting on its own; the poll below is
            // the safety net in the meantime.
        });

        // Fallback poll (also keeps relative "Xm ago" labels fresh).
        const pollId = setInterval(() => { if (active) refreshNotifications(); }, 30000);

        return () => {
            active = false;
            source?.close();
            clearInterval(pollId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshNotifications]);

    const markAllNotificationsRead = () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnreadCount(0);
        backendApi.notifications.markAllRead().catch(() => {});
    };

    const handleNotificationClick = (n) => {
        if (!n.read) {
            setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
            setUnreadCount((prev) => Math.max(0, prev - 1));
            backendApi.notifications.markRead(n.id).catch(() => {});
        }
        if (n.link) {
            setIsNotifOpen(false);
            setCurrentPage(n.link);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setIsProfileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutsideNotif = (event) => {
            if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) {
                setIsNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideNotif);
        return () => document.removeEventListener('mousedown', handleClickOutsideNotif);
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
            if (isClockedIn && checkInTime) {
                const now = new Date();
                const liveSeconds = Math.floor((now.getTime() - checkInTime.getTime()) / 1000);
                setElapsedTime(formatSecondsToHMS(accumulatedSeconds + liveSeconds));
                setElapsedSeconds(accumulatedSeconds + liveSeconds);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [isClockedIn, checkInTime, accumulatedSeconds]);

    // Feed cards along the bottom of the dashboard.
    useEffect(() => {
        let active = true;
        const guard = (setter) => (rows) => active && setter(Array.isArray(rows) ? rows : []);
        backendApi.entities.Employee.list().then(guard(setEmployees));
        backendApi.entities.Payroll.list().then(guard(setPayrolls));
        backendApi.entities.Document.list().then(guard(setDocuments));
        backendApi.entities.LeaveRequest.list().then(guard(setLeaveRequests));
        return () => { active = false; };
    }, []);

    // ---- time formatting -------------------------------------------------
    const fullFormattedTime = currentTime.toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    const clock = fullFormattedTime.slice(0, -3);   // "02:47:14"
    const ampm = fullFormattedTime.slice(-2);       // "PM"

    const shortDate = currentTime.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: '2-digit' });
    const longDate = currentTime.toLocaleDateString('en-US', {
        weekday: 'long', month: 'short', day: '2-digit', year: 'numeric',
    });
    const greeting = currentTime.getHours() < 12 ? 'Good morning'
        : currentTime.getHours() < 18 ? 'Good afternoon' : 'Good evening';

    const [punchBusy, setPunchBusy] = useState(false);
    const punchInFlightRef = React.useRef(false);

    useEffect(() => {
        let active = true;

        const loadAttendance = async () => {
            const todayData = await backendApi.attendance.today();
            if (!active) return;
            const seconds = todayData.accumulated_seconds || 0;
            setAccumulatedSeconds(seconds);
            if (todayData.status === 'clocked_in' && todayData.current_punch_in) {
                const punchInDate = new Date(todayData.current_punch_in);
                setIsClockedIn(true);
                setCheckInTime(punchInDate);
                const liveSeconds = seconds + (Date.now() - punchInDate.getTime()) / 1000;
                setElapsedTime(formatSecondsToHMS(liveSeconds));
                setElapsedSeconds(liveSeconds);
            } else {
                setIsClockedIn(false);
                setCheckInTime(null);
                setElapsedTime(formatSecondsToHMS(seconds));
                setElapsedSeconds(seconds);
            }
        };

        loadAttendance();
        window.addEventListener('focus', loadAttendance);

        return () => {
            active = false;
            window.removeEventListener('focus', loadAttendance);
        };
    }, []);

    const handlePunch = async () => {
        if (punchInFlightRef.current) return;
        punchInFlightRef.current = true;
        setPunchBusy(true);
        try {
            if (isClockedIn) {
                await backendApi.attendance.punchOut();
                const sessionSeconds = checkInTime ? Math.floor((Date.now() - checkInTime.getTime()) / 1000) : 0;
                const newTotal = accumulatedSeconds + sessionSeconds;
                setAccumulatedSeconds(newTotal);
                setElapsedTime(formatSecondsToHMS(newTotal));
                setElapsedSeconds(newTotal);
                setIsClockedIn(false);
                setCheckInTime(null);
                showToast('Punched out successfully', 'punchout');
            } else {
                await backendApi.attendance.punchIn();
                setCheckInTime(new Date());
                setIsClockedIn(true);
                setElapsedTime(formatSecondsToHMS(accumulatedSeconds));
                setElapsedSeconds(accumulatedSeconds);
                showToast('Punched in successfully', 'success');
            }
        } catch (err) {
            showToast(`Attendance failed: ${err.message || 'Unable to reach the server.'}`, 'error');
        } finally {
            setPunchBusy(false);
            punchInFlightRef.current = false;
        }
    };

    // ---- identity & role -------------------------------------------------
    const storedUser = backendApi.auth.getStoredUser();
    const role = storedUser?.role || 'employee';
    const isAdmin = ['admin', 'hr'].includes(role);
    const canManageTeam = isAdmin || role === 'manager';
    const roleLabel = isAdmin ? 'Admin' : role === 'manager' ? 'Manager' : 'Employee';
    const displayName = userName || storedUser?.full_name || storedUser?.name || 'User';

    // ---- navigation ------------------------------------------------------
    // Grouped like the reference layout; org-wide entries stay role-gated so
    // nobody gains access they did not have before.
    const navGroups = [
        {
            label: 'Overview',
            items: [
                { name: 'Dashboard', icon: Home },
                ...(canManageTeam ? [{ name: 'Employees', icon: Users }] : []),
                ...(canManageTeam ? [{ name: 'Attendance', icon: Clock }] : []),
                { name: 'My Attendance', icon: ClipboardList },
                { name: 'Leave', icon: Calendar },
                { name: 'Payroll', icon: DollarSign },
                { name: 'Performance', icon: Target },
                { name: 'Documents', icon: FileText },
            ],
        },
        {
            label: 'Requests',
            items: [
                { name: 'Raise Request', icon: Inbox, action: () => setIsTicketModalOpen(true) },
                { name: 'My Tickets', icon: FileSignature },
                ...(isAdmin ? [{ name: 'Access Requests', icon: ShieldCheck }] : []),
            ],
        },
        {
            label: 'Tools',
            items: [{ name: 'AI Assistant', icon: Sparkles, badge: 'AI' }],
        },
        {
            label: 'Settings',
            items: [
                { name: 'My Profile', icon: User },
                { name: 'Notifications', icon: Bell },
                { name: 'Integrations', icon: Plug },
                { name: 'Settings', icon: Settings },
            ],
        },
    ];

    const quickActions = [
        { title: 'Raise Ticket', icon: Zap, action: () => setIsTicketModalOpen(true) },
        { title: 'Apply for Leave', icon: Calendar, action: () => setIsLeaveModalOpen(true) },
        { title: 'View Payslip', icon: Receipt, action: () => setCurrentPage('Payroll') },
        { title: 'My Documents', icon: FolderOpen, action: () => setCurrentPage('Documents') },
        { title: 'View Attendance', icon: ClipboardList, action: () => setCurrentPage('My Attendance') },
        { title: 'My Tickets', icon: FileSignature, action: () => setCurrentPage('My Tickets') },
        { title: 'AI Assistant', icon: Sparkles, action: () => setCurrentPage('AI Assistant') },
        { title: 'My Profile', icon: User, action: () => setCurrentPage('My Profile') },
    ];

    // ---- derived summary numbers ----------------------------------------
    const leavesTaken = (leaveRequests || []).filter(
        (r) => String(r.status || '').toLowerCase() === 'approved'
    ).length;
    const tasksDone = tasks.filter((t) => t.done).length;

    const summaryTiles = [
        { icon: Clock, label: 'Working Hours', value: formatDurationFromSeconds(elapsedSeconds) },
        { icon: Users, label: 'Leaves Taken', value: String(leavesTaken).padStart(2, '0') },
        { icon: LogIn, label: 'Attendance', value: isClockedIn || accumulatedSeconds > 0 ? 'Marked' : 'Not Marked' },
        { icon: CheckSquare, label: 'Tasks Completed', value: `${tasksDone} / ${tasks.length}` },
    ];

    // Static UI content, the backend exposes no announcements/events feed yet.
    const announcements = [
        {
            title: 'Annual Health Check-up',
            body: 'Health check-up for all employees is scheduled on Aug 28 & 29.',
        },
    ];
    const upcomingEvents = [
        { icon: Users, title: 'Team Meeting', when: 'Weekly · 10:00 AM' },
        { icon: DollarSign, title: 'Payroll Release', when: 'Month end · 05:00 PM' },
        { icon: TrendingUp, title: 'Performance Review', when: 'Quarterly · 11:00 AM' },
    ];

    const askAI = (question) => {
        if (question) setAiQuestion(question);
        setCurrentPage('AI Assistant');
    };

    // ---- dashboard body --------------------------------------------------
    const renderDashboard = () => (
        <>
            {/* Row 1, clock, quick actions, today */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Panel title="Time Tracker" icon={Clock}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="flex items-baseline" style={{ color: T.ink }}>
                                <span className="hrms-clock text-[38px]">{clock.slice(0, 5)}</span>
                                <span className="hrms-clock text-[24px]">{clock.slice(5)}</span>
                                <span className="text-[15px] font-semibold ml-1.5" style={{ color: T.muted }}>{ampm}</span>
                            </div>
                            <p className="text-[12px] mt-1" style={{ color: T.muted }}>{shortDate}</p>
                        </div>
                        <div className="flex flex-col items-center gap-2">
                            <button onClick={handlePunch} disabled={punchBusy}
                                className={isClockedIn ? 'hrms-btn-punch-out' : 'hrms-btn-punch-in'}>
                                {isClockedIn
                                    ? <LogOut className="w-4 h-4" strokeWidth={2.2} />
                                    : <LogIn className="w-4 h-4" strokeWidth={2.2} />}
                                {punchBusy ? 'Working…' : isClockedIn ? 'Punch Out' : 'Punch In'}
                            </button>
                            <span className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: T.muted }}>
                                <span className="w-1.5 h-1.5 rounded-full"
                                    style={{ background: isClockedIn ? SERIES.present : T.faint }} />
                                {isClockedIn ? 'Clocked In' : 'Clocked Out'}
                            </span>
                        </div>
                    </div>
                    <div className="mt-5 pt-4 border-t" style={{ borderColor: T.lineSoft }}>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: T.muted }}>
                            Elapsed Time
                        </p>
                        <p className="hrms-clock text-[24px] mt-1" style={{ color: T.ink }}>{elapsedTime}</p>
                    </div>
                </Panel>

                <Panel title="Quick Actions" icon={Zap}>
                    <div className="grid grid-cols-4 gap-2.5">
                        {quickActions.map((a) => (
                            <button key={a.title} onClick={a.action} className="hrms-quick">
                                <span className="hrms-quick-icon">
                                    <a.icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
                                </span>
                                <span className="hrms-quick-label">{a.title}</span>
                            </button>
                        ))}
                    </div>
                </Panel>

                <Panel title="Today's Summary" icon={CalendarDays}
                    footer="View Full Summary" onFooter={() => setCurrentPage('My Attendance')}>
                    <div className="grid grid-cols-4 gap-2">
                        {summaryTiles.map((s) => (
                            <div key={s.label} className="flex flex-col items-center text-center">
                                <span className="hrms-summary-icon">
                                    <s.icon className="w-[17px] h-[17px]" strokeWidth={1.7} />
                                </span>
                                <span className="text-[10px] font-medium mt-2 leading-tight" style={{ color: T.muted }}>
                                    {s.label}
                                </span>
                                <span className="text-[13px] font-bold mt-1 leading-tight" style={{ color: T.ink }}>
                                    {s.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>

            {/* Row 2, charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-5">
                <LeaveBalanceChart onViewDetails={() => setCurrentPage('Leave')} />
                <AttendanceOverview onViewAttendance={() => setCurrentPage('My Attendance')} />
                <PerformanceRadar onViewPerformance={() => setCurrentPage('Performance')} />

                <Panel title="Upcoming Events" icon={Calendar}
                    footer="View Calendar" onFooter={() => setCurrentPage('My Attendance')}>
                    <ul className="divide-y" style={{ borderColor: T.lineSoft }}>
                        {upcomingEvents.map((e) => (
                            <li key={e.title} className="flex items-center gap-3 py-3 first:pt-0">
                                <span className="hrms-list-icon">
                                    <e.icon className="w-[15px] h-[15px]" strokeWidth={1.8} />
                                </span>
                                <div className="min-w-0">
                                    <p className="text-[13px] font-semibold truncate" style={{ color: T.ink }}>{e.title}</p>
                                    <p className="text-[11px]" style={{ color: T.muted }}>{e.when}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Panel>
            </div>

            {/* Row 3, announcements, tasks, assistant */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-5">
                <Panel title="Announcements" icon={Megaphone}
                    footer="View All" onFooter={() => setCurrentPage('Documents')}>
                    {announcements.map((a) => (
                        <div key={a.title} className="rounded-xl p-4 border-l-[3px]"
                            style={{ background: T.greenTint, borderColor: T.green }}>
                            <p className="text-[14px] font-bold" style={{ color: T.ink }}>{a.title}</p>
                            <p className="text-[12.5px] mt-1.5 leading-relaxed" style={{ color: T.body }}>{a.body}</p>
                        </div>
                    ))}
                </Panel>

                <Panel title="My Tasks" icon={CheckSquare}
                    footer="View All Tasks" onFooter={() => setCurrentPage('My Tickets')}>
                    <ul className="space-y-1">
                        {tasks.map((t) => (
                            <li key={t.id} className="flex items-center justify-between gap-3 py-1.5">
                                <label className="flex items-center gap-2.5 cursor-pointer min-w-0">
                                    <input type="checkbox" checked={t.done} className="hrms-check"
                                        onChange={() => setTasks((prev) =>
                                            prev.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)))} />
                                    <span className={`text-[13px] truncate ${t.done ? 'line-through' : ''}`}
                                        style={{ color: t.done ? T.faint : T.body }}>{t.title}</span>
                                </label>
                                <StatusPill status={t.done ? 'Done' : 'Pending'} />
                            </li>
                        ))}
                    </ul>
                </Panel>

                {/* AI assistant, tinted so it reads as the one interactive card */}
                <section className="hrms-card flex flex-col" style={{ background: T.greenTint, borderColor: '#DCE7DF' }}>
                    <div className="px-5 pt-4 pb-3">
                        <CardTitle icon={Sparkles}>AI Assistant</CardTitle>
                    </div>
                    <div className="px-5 pb-5 flex-1 flex flex-col">
                        <p className="text-[14px] font-bold" style={{ color: T.ink }}>How can I help you today?</p>
                        <p className="text-[12px] mt-1" style={{ color: T.muted }}>
                            Ask anything about HR policies, leaves, payroll, and more.
                        </p>
                        <form className="mt-4 relative"
                            onSubmit={(e) => { e.preventDefault(); askAI(aiQuestion); }}>
                            <input value={aiQuestion} onChange={(e) => setAiQuestion(e.target.value)}
                                placeholder="Type your question..." className="hrms-ai-input" />
                            <button type="submit" className="hrms-ai-send" aria-label="Ask the assistant">
                                <Send className="w-4 h-4" strokeWidth={1.9} />
                            </button>
                        </form>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {['Leave Policy', 'Payslip Download', 'Attendance Rules'].map((chip) => (
                                <button key={chip} type="button" onClick={() => askAI(chip)} className="hrms-chip">
                                    {chip}
                                </button>
                            ))}
                        </div>
                    </div>
                </section>
            </div>

            {/* Row 4, recent records */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-5">
                <Panel title="Recent Employees" icon={Users}
                    right={<ViewAll onClick={() => setCurrentPage(canManageTeam ? 'Employees' : 'My Profile')} />}>
                    {employees === null ? <EmptyRow>Loading…</EmptyRow>
                        : employees.length === 0 ? <EmptyRow>No employees yet</EmptyRow>
                            : <ul className="divide-y" style={{ borderColor: T.lineSoft }}>
                                {employees.slice(0, 4).map((e) => {
                                    const name = `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || 'Unnamed';
                                    return (
                                        <li key={e.id} className="flex items-center gap-3 py-2.5 first:pt-0">
                                            <span className="hrms-avatar">{initials(name)}</span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[12.5px] font-semibold truncate" style={{ color: T.ink }}>{name}</p>
                                                <p className="text-[11px] truncate" style={{ color: T.muted }}>{e.position || ','}</p>
                                            </div>
                                            <span className="text-[11px] whitespace-nowrap" style={{ color: T.faint }}>
                                                {fmtDate(e.join_date)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>}
                </Panel>

                <Panel title="Recent Payrolls" icon={Receipt}
                    right={<ViewAll onClick={() => setCurrentPage('Payroll')} />}>
                    {payrolls === null ? <EmptyRow>Loading…</EmptyRow>
                        : payrolls.length === 0 ? <EmptyRow>No payroll runs yet</EmptyRow>
                            : <ul className="divide-y" style={{ borderColor: T.lineSoft }}>
                                {payrolls.slice(0, 4).map((p) => (
                                    <li key={p.id} className="flex items-center gap-3 py-2.5 first:pt-0">
                                        <span className="hrms-list-icon">
                                            <Receipt className="w-[14px] h-[14px]" strokeWidth={1.8} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[12.5px] font-semibold truncate" style={{ color: T.ink }}>
                                                {p.month} {p.year}
                                            </p>
                                            <p className="text-[11px] truncate" style={{ color: T.muted }}>
                                                {p.pay_date ? `Paid on ${fmtDate(p.pay_date)}` : p.status}
                                            </p>
                                        </div>
                                        <span className="text-[12px] font-bold whitespace-nowrap" style={{ color: T.ink }}>
                                            {fmtMoney(p.net_salary)}
                                        </span>
                                    </li>
                                ))}
                            </ul>}
                </Panel>

                <Panel title="Recent Documents" icon={FileText}
                    right={<ViewAll onClick={() => setCurrentPage('Documents')} />}>
                    {documents === null ? <EmptyRow>Loading…</EmptyRow>
                        : documents.length === 0 ? <EmptyRow>No documents yet</EmptyRow>
                            : <ul className="divide-y" style={{ borderColor: T.lineSoft }}>
                                {documents.slice(0, 4).map((d) => (
                                    <li key={d.id} className="flex items-center gap-3 py-2.5 first:pt-0">
                                        <span className="hrms-list-icon" style={{ background: '#FCEEEE', color: '#C0413D' }}>
                                            <FileText className="w-[14px] h-[14px]" strokeWidth={1.8} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[12.5px] font-semibold truncate" style={{ color: T.ink }}>{d.title}</p>
                                            <p className="text-[11px] truncate" style={{ color: T.muted }}>
                                                {(d.file_type || d.category || 'File')} · Uploaded on {fmtDate(d.created_at)}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>}
                </Panel>

                <Panel title="My Requests" icon={FileSignature}
                    right={<ViewAll onClick={() => setCurrentPage('My Tickets')} />}>
                    {leaveRequests === null ? <EmptyRow>Loading…</EmptyRow>
                        : leaveRequests.length === 0 ? <EmptyRow>No requests yet</EmptyRow>
                            : <ul className="divide-y" style={{ borderColor: T.lineSoft }}>
                                {leaveRequests.slice(0, 4).map((r) => (
                                    <li key={r.id} className="flex items-center gap-2.5 py-2.5 first:pt-0">
                                        <span className="hrms-list-icon">
                                            <Calendar className="w-[14px] h-[14px]" strokeWidth={1.8} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-[12.5px] font-semibold truncate" style={{ color: T.ink }}>
                                                {r.leave_type || 'Leave Request'}
                                            </p>
                                            <p className="text-[11px]" style={{ color: T.muted }}>{fmtDate(r.start_date)}</p>
                                        </div>
                                        <StatusPill status={r.status} />
                                    </li>
                                ))}
                            </ul>}
                </Panel>
            </div>
        </>
    );

    // ---- page router -----------------------------------------------------
    const renderPage = () => {
        switch (currentPage) {
            case 'Employees': return <EmployeesPage />;
            case 'Attendance': return <AttendancePage />;
            case 'Leave': return <LeavePage />;
            case 'Payroll': return <PayrollPage />;
            case 'Performance': return <PerformancePage />;
            case 'Documents': return <DocumentsPage />;
            case 'My Attendance': return <MyAttendancePage />;
            case 'Access Requests': return <AccessRequestsPage />;
            case 'AI Assistant': return <AIAssistant initialPrompt={aiQuestion} />;
            case 'My Profile': return <ProfilePage />;
            case 'Settings': return <SettingsPage />;
            case 'My Tickets': return <MyTicketsPage onRaiseTicket={() => setIsTicketModalOpen(true)} />;
            case 'Notifications':
                return <NotificationsPage
                    notifications={notifications}
                    unreadCount={unreadCount}
                    onMarkAllRead={markAllNotificationsRead}
                    onOpen={handleNotificationClick}
                />;
            case 'Integrations':
                return <NotConfiguredPage title="Integrations" icon={Plug}
                    subtitle="Connect the HRMS to the tools your team already uses."
                    message="No integrations have been connected to this workspace yet." />;
            default: return renderDashboard();
        }
    };

    const onDashboard = currentPage === 'Dashboard';
    // Imported pages bring their own page padding; locally-defined ones do not.
    const needsPadding = ['Notifications', 'Integrations'].includes(currentPage);

    return (
        <div className="hrms-root flex h-screen overflow-hidden">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .hrms-root {
            font-family: 'Plus Jakarta Sans', system-ui, -apple-system, sans-serif;
            background: ${T.bg};
            color: ${T.ink};
            -webkit-font-smoothing: antialiased;
        }

        /* ---------- surfaces ---------- */
        .hrms-card {
            background: ${T.card};
            border: 1px solid ${T.line};
            border-radius: 14px;
            box-shadow: 0 1px 2px rgba(21,24,28,0.04);
        }
        .hrms-label {
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.09em;
            text-transform: uppercase;
            color: ${T.muted};
        }
        .hrms-note { font-size: 11px; font-weight: 500; color: ${T.faint}; }
        .hrms-card-foot {
            display: flex;
            align-items: center;
            gap: 6px;
            width: 100%;
            padding: 11px 20px;
            border-top: 1px solid ${T.lineSoft};
            font-size: 12px;
            font-weight: 600;
            color: ${T.green};
            transition: background 0.15s ease;
        }
        .hrms-card-foot:hover { background: ${T.greenTint}; }

        .hrms-viewall {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-size: 11px;
            font-weight: 700;
            color: ${T.green};
            white-space: nowrap;
        }
        .hrms-viewall:hover { text-decoration: underline; }

        /* ---------- sidebar ---------- */
        .hrms-sidebar {
            width: 216px;
            background: ${T.card};
            border-right: 1px solid ${T.line};
        }
        .hrms-group-label {
            font-size: 9.5px;
            font-weight: 700;
            letter-spacing: 0.16em;
            text-transform: uppercase;
            color: ${T.faint};
            padding: 0 12px;
            margin-bottom: 6px;
        }
        .hrms-nav {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 8px 12px;
            border-radius: 9px;
            font-size: 13px;
            font-weight: 500;
            color: ${T.body};
            transition: background 0.15s ease, color 0.15s ease;
        }
        .hrms-nav:hover { background: #F4F4F0; color: ${T.ink}; }
        .hrms-nav--active {
            background: ${T.green};
            color: #FFFFFF;
            font-weight: 600;
        }
        .hrms-nav--active:hover { background: ${T.greenDeep}; color: #FFFFFF; }
        .hrms-nav-badge {
            margin-left: auto;
            font-size: 8.5px;
            font-weight: 800;
            letter-spacing: 0.08em;
            padding: 2px 5px;
            border-radius: 4px;
            background: ${T.greenTint};
            color: ${T.green};
        }
        .hrms-nav--active .hrms-nav-badge {
            background: rgba(255,255,255,0.22);
            color: #FFFFFF;
        }

        /* ---------- buttons ---------- */
        .hrms-btn-primary {
            padding: 9px 16px;
            border-radius: 9px;
            background: ${T.green};
            color: #fff;
            font-size: 13px;
            font-weight: 600;
            transition: background 0.15s ease;
        }
        .hrms-btn-primary:hover { background: ${T.greenDeep}; }
        .hrms-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

        .hrms-btn-ghost {
            padding: 9px 16px;
            border-radius: 9px;
            border: 1px solid ${T.line};
            background: #fff;
            font-size: 13px;
            font-weight: 600;
            color: ${T.body};
            transition: background 0.15s ease;
        }
        .hrms-btn-ghost:hover { background: #F4F4F0; }

        .hrms-topbtn-solid {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            height: 36px;
            padding: 0 15px;
            border-radius: 9px;
            background: ${T.green};
            color: #fff;
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            transition: background 0.15s ease;
        }
        .hrms-topbtn-solid:hover { background: ${T.greenDeep}; }

        .hrms-topbtn-outline {
            display: inline-flex;
            align-items: center;
            height: 36px;
            padding: 0 15px;
            border-radius: 9px;
            border: 1px solid ${T.line};
            background: #fff;
            color: ${T.ink};
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            transition: background 0.15s ease;
        }
        .hrms-topbtn-outline:hover { background: #F4F4F0; }

        .hrms-btn-punch-in, .hrms-btn-punch-out {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 10px 22px;
            border-radius: 10px;
            color: #fff;
            font-size: 12px;
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            transition: background 0.15s ease;
        }
        .hrms-btn-punch-in { background: ${T.green}; }
        .hrms-btn-punch-in:hover { background: ${T.greenDeep}; }
        .hrms-btn-punch-out { background: #B4433F; }
        .hrms-btn-punch-out:hover { background: #993733; }
        .hrms-btn-punch-in:disabled, .hrms-btn-punch-out:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ---------- quick actions ---------- */
        .hrms-quick {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 7px;
            padding: 12px 4px;
            border-radius: 11px;
            border: 1px solid ${T.lineSoft};
            background: #fff;
            transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
        }
        .hrms-quick:hover {
            border-color: ${T.green};
            background: ${T.greenTint};
            transform: translateY(-1px);
        }
        .hrms-quick-icon { color: ${T.green}; }
        .hrms-quick-label {
            font-size: 10px;
            font-weight: 600;
            line-height: 1.25;
            text-align: center;
            color: ${T.body};
        }

        /* ---------- misc bits ---------- */
        .hrms-summary-icon,
        .hrms-list-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 9px;
            background: ${T.greenTint};
            color: ${T.green};
            flex-shrink: 0;
        }
        .hrms-summary-icon { width: 38px; height: 38px; border-radius: 50%; }
        .hrms-list-icon { width: 28px; height: 28px; }

        .hrms-avatar {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 30px; height: 30px;
            border-radius: 50%;
            background: ${T.green};
            color: #fff;
            font-size: 10.5px;
            font-weight: 700;
            flex-shrink: 0;
        }

        .hrms-check {
            width: 15px; height: 15px;
            accent-color: ${T.green};
            border-radius: 4px;
            flex-shrink: 0;
        }

        .hrms-clock {
            font-weight: 800;
            letter-spacing: -0.02em;
            font-variant-numeric: tabular-nums;
            line-height: 1;
        }

        .hrms-ai-input {
            width: 100%;
            padding: 10px 40px 10px 13px;
            border-radius: 10px;
            border: 1px solid ${T.line};
            background: #fff;
            font-size: 12.5px;
            color: ${T.ink};
            outline: none;
        }
        .hrms-ai-input::placeholder { color: ${T.faint}; }
        .hrms-ai-input:focus { border-color: ${T.green}; }
        .hrms-ai-send {
            position: absolute;
            right: 6px; top: 50%;
            transform: translateY(-50%);
            padding: 6px;
            border-radius: 8px;
            color: ${T.green};
            transition: background 0.15s ease;
        }
        .hrms-ai-send:hover { background: ${T.greenTint}; }

        .hrms-chip {
            padding: 5px 11px;
            border-radius: 999px;
            border: 1px solid ${T.line};
            background: #fff;
            font-size: 10.5px;
            font-weight: 600;
            color: ${T.body};
            transition: border-color 0.15s ease, color 0.15s ease;
        }
        .hrms-chip:hover { border-color: ${T.green}; color: ${T.green}; }

        /* ---------- dropdowns ---------- */
        @keyframes hrms-dropdown-in {
            from { opacity: 0; transform: translateY(-6px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .hrms-dropdown {
            position: absolute;
            right: 0;
            margin-top: 8px;
            border-radius: 14px;
            overflow: hidden;
            background: #fff;
            border: 1px solid ${T.line};
            box-shadow: 0 24px 50px -18px rgba(15,25,20,0.35);
            animation: hrms-dropdown-in 0.15s ease-out;
            z-index: 9999;
        }
        .hrms-dropdown-item {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            padding: 10px 16px;
            font-size: 13px;
            font-weight: 600;
            color: ${T.body};
            transition: background 0.15s ease, color 0.15s ease;
        }
        .hrms-dropdown-item:hover { background: ${T.greenTint}; color: ${T.green}; }

        /* ---------- form fields ---------- */
        .hrms-field-label {
            display: block;
            font-size: 12px;
            font-weight: 600;
            color: ${T.body};
            margin-bottom: 6px;
        }
        .hrms-input {
            width: 100%;
            padding: 9px 12px;
            border-radius: 9px;
            border: 1px solid ${T.line};
            background: #fff;
            font-size: 13px;
            color: ${T.ink};
            outline: none;
        }
        .hrms-input:focus { border-color: ${T.green}; box-shadow: 0 0 0 3px ${T.greenTint}; }

        /* ---------- toast ---------- */
        @keyframes hrms-toast-in {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
        }

        /* ---------- scrollbars ---------- */
        .hrms-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .hrms-scroll::-webkit-scrollbar-thumb { background: #DFDED8; border-radius: 999px; }
        .hrms-scroll::-webkit-scrollbar-track { background: transparent; }
      `}</style>

            {/* ============ SIDEBAR ============ */}
            <aside className="hrms-sidebar flex flex-col flex-shrink-0">
                <div className="flex items-center gap-2.5 px-4 h-[68px] flex-shrink-0 cursor-pointer"
                    style={{ borderBottom: `1px solid ${T.line}` }}
                    onClick={() => setCurrentPage('Dashboard')}>
                    <img src="/logo 2.jpg" alt="WebIdeal"
                        className="w-9 h-9 rounded-lg object-contain flex-shrink-0"
                        style={{ background: T.greenTint, padding: 3 }} />
                    <div className="min-w-0">
                        <p className="text-[15px] font-extrabold leading-none tracking-tight" style={{ color: T.green }}>
                            WEBIDEAL
                        </p>
                        <p className="text-[8.5px] font-bold uppercase tracking-[0.16em] mt-1 truncate"
                            style={{ color: T.faint }}>
                            HRMS · {roleLabel}
                        </p>
                    </div>
                </div>

                <nav className="hrms-scroll flex-1 overflow-y-auto px-3 py-4 space-y-5">
                    {navGroups.map((group) => (
                        <div key={group.label}>
                            <p className="hrms-group-label">{group.label}</p>
                            <div className="space-y-0.5">
                                {group.items.map((item) => {
                                    const active = currentPage === item.name;
                                    return (
                                        <button key={item.name}
                                            onClick={item.action ? item.action : () => setCurrentPage(item.name)}
                                            className={`hrms-nav ${active ? 'hrms-nav--active' : ''}`}>
                                            <item.icon className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={1.7} />
                                            <span className="truncate">{item.name}</span>
                                            {item.badge && <span className="hrms-nav-badge">{item.badge}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                <div className="p-3.5 flex-shrink-0" style={{ borderTop: `1px solid ${T.line}` }}>
                    <div className="flex items-center gap-1.5 mb-2">
                        <HelpCircle className="w-3 h-3" style={{ color: T.faint }} strokeWidth={2} />
                        <span className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: T.faint }}>
                            Need help?
                        </span>
                    </div>
                    <button onClick={() => setIsTicketModalOpen(true)}
                        className="w-full h-9 rounded-lg text-[10px] font-bold uppercase tracking-[0.12em] text-white transition-colors"
                        style={{ background: T.green }}>
                        Contact Support
                    </button>
                    <p className="text-[9px] mt-3 text-center" style={{ color: T.faint }}>
                        © {new Date().getFullYear()} Webideal. All rights reserved.
                    </p>
                </div>
            </aside>

            {/* ============ MAIN ============ */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="flex items-center justify-between gap-4 px-7 h-[68px] flex-shrink-0"
                    style={{ background: T.card, borderBottom: `1px solid ${T.line}` }}>
                    <div className="min-w-0">
                        <h1 className="text-[19px] font-bold tracking-tight truncate" style={{ color: T.ink }}>
                            {onDashboard ? `${greeting}, ${displayName} 👋` : currentPage}
                        </h1>
                        <p className="text-[11.5px] mt-0.5" style={{ color: T.muted }}>{longDate}</p>
                    </div>

                    <div className="flex items-center gap-2.5 flex-shrink-0">
                        <button onClick={() => setCurrentPage('AI Assistant')} className="hrms-topbtn-solid">
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />
                            WebIdeal AI
                        </button>
                        <button onClick={() => setIsTicketModalOpen(true)} className="hrms-topbtn-outline">
                            Raise Ticket
                        </button>

                        {/* Notification bell + dropdown */}
                        <div className="relative" ref={notifMenuRef}>
                            <button onClick={() => setIsNotifOpen((prev) => !prev)}
                                className="relative w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-[#F4F4F0]"
                                aria-label="Notifications">
                                <Bell className="w-[18px] h-[18px]" style={{ color: T.body }} strokeWidth={1.8} />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center text-[9px] font-bold text-white rounded-full"
                                        style={{ minWidth: 15, height: 15, padding: '0 4px', background: '#D9534F', lineHeight: 1 }}>
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </button>

                            {isNotifOpen && (
                                <div className="hrms-dropdown w-80">
                                    <div className="flex items-center justify-between px-4 py-3"
                                        style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                                        <span className="hrms-label">Notifications</span>
                                        {unreadCount > 0 && (
                                            <button onClick={markAllNotificationsRead} className="hrms-viewall">
                                                Mark all read
                                            </button>
                                        )}
                                    </div>

                                    <div className="hrms-scroll max-h-80 overflow-y-auto">
                                        {notifications.length === 0 ? (
                                            <div className="px-4 py-6 text-center text-[13px]" style={{ color: T.faint }}>
                                                No notifications yet
                                            </div>
                                        ) : (
                                            notifications.map((n) => {
                                                const NotifIcon = NOTIF_ICONS[n.type] || Bell;
                                                return (
                                                    <div key={n.id}
                                                        onClick={() => handleNotificationClick(n)}
                                                        className="px-4 py-3 flex gap-3 items-start transition-colors hover:bg-[#FAFAF7] cursor-pointer"
                                                        style={{
                                                            borderBottom: `1px solid ${T.lineSoft}`,
                                                            background: n.read ? 'transparent' : T.greenTint,
                                                        }}>
                                                        <span className="hrms-list-icon mt-0.5">
                                                            <NotifIcon className="w-[14px] h-[14px]" strokeWidth={2} />
                                                        </span>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="text-[13px] font-semibold" style={{ color: T.ink }}>{n.title}</div>
                                                            <div className="text-[12px] mt-0.5" style={{ color: T.muted }}>{n.message}</div>
                                                            <div className="text-[10px] font-bold uppercase tracking-[0.08em] mt-1"
                                                                style={{ color: T.faint }}>{n.time}</div>
                                                        </div>
                                                        {!n.read && (
                                                            <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                                                                style={{ background: T.green }} />
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    <div style={{ borderTop: `1px solid ${T.lineSoft}` }}>
                                        <button
                                            onClick={() => { setIsNotifOpen(false); setIsTicketModalOpen(true); }}
                                            className="hrms-dropdown-item justify-center"
                                            style={{ color: T.green }}>
                                            <Ticket className="w-3.5 h-3.5" strokeWidth={1.8} />
                                            Raise a ticket
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Profile chip + dropdown */}
                        <div className="relative" ref={profileMenuRef}>
                            <button onClick={() => setIsProfileMenuOpen((prev) => !prev)}
                                className="flex items-center gap-2.5 pl-2 pr-1 py-1 rounded-lg transition-colors hover:bg-[#F4F4F0]">
                                <span className="hrms-avatar" style={{ width: 34, height: 34, fontSize: 12 }}>
                                    {initials(displayName)}
                                </span>
                                <span className="text-left hidden sm:block">
                                    <span className="block text-[12.5px] font-bold leading-none" style={{ color: T.ink }}>
                                        {displayName}
                                    </span>
                                    <span className="block text-[10px] font-semibold mt-1" style={{ color: T.faint }}>
                                        {storedUser?.employee_id || role}
                                    </span>
                                </span>
                                <ChevronDown className="w-3.5 h-3.5" style={{ color: T.faint }} strokeWidth={2} />
                            </button>

                            {isProfileMenuOpen && (
                                <div className="hrms-dropdown w-56">
                                    <div className="px-4 py-3" style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
                                        <div className="text-[13px] font-bold" style={{ color: T.ink }}>{displayName}</div>
                                        <div className="text-[11.5px] truncate" style={{ color: T.muted }}>{storedUser?.email || ''}</div>
                                    </div>

                                    <button onClick={() => { setIsProfileMenuOpen(false); setCurrentPage('My Profile'); }}
                                        className="hrms-dropdown-item">
                                        <User className="w-4 h-4" strokeWidth={1.8} />
                                        My Profile
                                    </button>
                                    <button onClick={() => { setIsProfileMenuOpen(false); setCurrentPage('Settings'); }}
                                        className="hrms-dropdown-item">
                                        <Settings className="w-4 h-4" strokeWidth={1.8} />
                                        Settings
                                    </button>
                                    <button onClick={() => { setIsProfileMenuOpen(false); setCurrentPage('My Tickets'); }}
                                        className="hrms-dropdown-item">
                                        <Ticket className="w-4 h-4" strokeWidth={1.8} />
                                        My Tickets
                                    </button>

                                    <div style={{ borderTop: `1px solid ${T.lineSoft}` }}>
                                        <button onClick={() => { setIsProfileMenuOpen(false); onLogout(); }}
                                            className="hrms-dropdown-item"
                                            style={{ color: '#B4433F' }}>
                                            <LogOut className="w-4 h-4" strokeWidth={1.8} />
                                            Sign Out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <main className={`hrms-scroll flex-1 overflow-y-auto ${onDashboard || needsPadding ? 'p-6' : ''}`}
                    style={{ background: T.bg }}>
                    {renderPage()}
                </main>
            </div>

            {/* --- MODAL RENDERING --- */}
            <RaiseTicketModal isOpen={isTicketModalOpen} onClose={() => setIsTicketModalOpen(false)} />
            <ApplyLeaveModal isOpen={isLeaveModalOpen} onClose={() => setIsLeaveModalOpen(false)} />

            {/* Toast notification */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-[9999] flex items-center gap-3 pl-3 pr-5 py-3 rounded-full"
                    style={{
                        animation: 'hrms-toast-in 0.2s ease-out',
                        background: '#ffffff',
                        border: `1px solid ${T.line}`,
                        boxShadow: '0 20px 45px -18px rgba(15,25,20,0.4)',
                    }}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                            background:
                                toast.type === 'error' ? '#FCEAEA' :
                                toast.type === 'success' ? '#E8F3EC' :
                                toast.type === 'punchout' ? '#FBEBE6' :
                                '#FAF3E0',
                        }}>
                        {toast.type === 'error' ? (
                            <AlertCircle className="w-4 h-4" style={{ color: '#C0413D' }} strokeWidth={2.2} />
                        ) : toast.type === 'success' ? (
                            <CheckCircle2 className="w-4 h-4" style={{ color: SERIES.present }} strokeWidth={2.2} />
                        ) : toast.type === 'punchout' ? (
                            <CheckCircle2 className="w-4 h-4" style={{ color: '#B4433F' }} strokeWidth={2.2} />
                        ) : (
                            <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#B98A2E' }} strokeWidth={2.5} />
                        )}
                    </div>
                    <span className="text-[13px] font-semibold"
                        style={{
                            color:
                                toast.type === 'error' ? '#B91C1C' :
                                toast.type === 'success' ? SERIES.present :
                                toast.type === 'punchout' ? '#B4433F' :
                                T.ink,
                        }}>
                        {toast.message}
                    </span>
                </div>
            )}
        </div>
    );
}
